<script>
    import { onMount } from "svelte";
    import axios from "axios";
    axios.defaults.xsrfHeaderName = "X-CSRF-TOKEN";
    axios.defaults.xsrfCookieName = "csrf_access_token";

    import Spinner from "../../components/Miscellaneous/Spinner.svelte";
    import { successToast, errorToast500 } from "../../utils/CommonToasts";
    import { delay } from "../../utils/Delay";

    import Input from "../../components/Inputs/Input.svelte";
    import Button from "../../components/Buttons/Button.svelte";
    import TextArea from "../../components/Inputs/TextArea.svelte";

    export let user;

    let isSubmitting = false;
    let isRecording = false;
    let preRecording = false;
    let secondsLeft = 60;
    let preCountdownSeconds = 3;
    let mediaRecorder;
    let audioURL;
    let localstream;

    onMount(async () => {
        if (user.idea.pitch_blob) {
            fetch(user.idea.pitch_blob)
                .then((res) => res.blob())
                .then((res) => (audioURL = URL.createObjectURL(res)));
        }
    });

    const startRecording = () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices
                .getUserMedia({ audio: true })
                .then(async function (stream) {
                    localstream = stream;

                    preRecording = true;
                    preCountdownTimer();

                    await delay(3000).then(() => {
                        preRecording = false;
                        isRecording = true;

                        countdownTimer();

                        mediaRecorder = new MediaRecorder(stream);
                        mediaRecorder.start();

                        let chunks = [];

                        mediaRecorder.ondataavailable = function (e) {
                            console.log(e.data);
                            chunks.push(e.data);
                        };

                        mediaRecorder.onstop = async function (e) {
                            const blob = new Blob(chunks, {
                                type: "audio/ogg; codecs=opus",
                            });

                            audioURL = window.URL.createObjectURL(blob);
                            chunks = [];

                            const reader = new FileReader();

                            reader.readAsDataURL(blob);

                            reader.onload = () => {
                                user.idea.pitch_blob = reader.result;
                                patchUser(user);
                            };
                        };
                    });
                })
                .catch(function (err) {
                    console.log(
                        "The following getUserMedia error occured: " + err
                    );
                });
        } else {
            alert("getUserMedia is not supported on your browser!");
        }
    };

    const stopRecording = () => {
        isRecording = false;
        secondsLeft = 60;
        preCountdownSeconds = 3;
        mediaRecorder.stop();
        localstream.getTracks().forEach(function (track) {
            track.stop();
        });
    };

    const submitHandler = async () => {
        isSubmitting = true;
        await axios
            .patch(
                APIBACKEND + "/user",
                { user: user },
                { withCredentials: true }
            )
            .then(function (res) {
                if (res.status === 200) {
                    successToast("Your pitch has been successfully saved");
                    setTimeout(function () {
                        isSubmitting = false;
                    }, 3500);
                } else {
                    errorToast500();
                }
            });
    };

    const preCountdownTimer = async () => {
        preCountdownSeconds--;
        if (preCountdownSeconds < 0 && preRecording) {
            preRecording = false;
        } else {
            await delay(1000).then(() => {
                preCountdownTimer();
            });
        }
    };

    const countdownTimer = async () => {
        secondsLeft--;
        if (secondsLeft < 0) {
            stopRecording();
        } else {
            await delay(1000).then(() => {
                countdownTimer();
            });
        }
    };

    const patchUser = (user) => {
        axios.patch(
            APIBACKEND + "/user",

            { user: user },
            { withCredentials: true }
        );
    };

    const deleteAudio = () => {
        secondsLeft = 5;
        preCountdownSeconds = 3;
        audioURL = null;
        user.idea.pitch_blob = null;
        patchUser(user);
    };
</script>

<style lang="scss">
    @import "../../styles/_mixins.scss";

    .profile {
        &-info {
            @include info-container;
            margin-bottom: var(--spacer-medium);
        }

        &-settings {
            margin: 0 auto;
            padding-left: var(--spacer-medium);
            padding-right: var(--spacer-medium);

            @include sm {
                margin-top: var(--spacer-large);
                align-self: flex-start;
                max-width: 712px;
                padding: 0;
            }

            :global(.input-container) {
                margin-bottom: var(--spacer-medium);
            }

            :global(.input-container):last-of-type {
                margin-bottom: var(--spacer-medium);
            }
        }
    }

    .recorder-container {
        @include input-container;
        flex-direction: row;
        flex-wrap: wrap;
        height: 130px;
        margin-bottom: var(--spacer-medium);

        h2 {
            font-size: var(--font-regular);
            font-weight: var(--weight-regular);
            width: 100%;
        }

        div {
            display: flex;
            align-items: center;
            width: 100%;
            padding: 0 var(--spacer-tiny);
        }

        .button {
            width: max-content;
        }

        p {
            margin-left: var(--spacer-small);
        }
    }

    .spinner-container {
        justify-content: space-around;

        p {
            font-size: var(--font-large);
            font-weight: var(--weight-medium);
        }
    }

    .audio-container {
        display: flex;
        justify-content: space-between;
    }
</style>

<form class="profile-settings" on:submit|preventDefault={submitHandler}>
    <div class="profile-info">
        <h2>What is pitching?</h2>
        <p>
            This section is what others will see in the discover tab. We only
            show your full profile after you've matched with someone. This
            ensures minimal bias when going through profiles.
        </p>

        <p>Q: Why a personal pitch?</p>
        <p>
            We are experimenting with the format of voice. Reading someone's
            profile can be tedious especially over a period of time. That's why
            we've introduced a minute voice pitch to see how it will affect the
            experience of discovering and connecting with new people.
        </p>
    </div>

    <TextArea
        name="bio"
        label="How would you describe yourself?"
        placeholder="Early stages of starting a startup, in the concept stage. BA in Robotics and looking for a co-founder to join the team."
        required
        bind:value={user.bio} />

    <TextArea
        name="ideal"
        label="Who would be your ideal co-founder?"
        placeholder="Someone who's interested in green tech energy!"
        required
        bind:value={user.ideal} />

    <div class="recorder-container">
        <h2>Personal Pitch</h2>
        <div>
            {#if preRecording}
                <div class="button">
                    <img alt="record button" src={'vectors/record.svg'} />
                </div>

                <div class="spinner-container">
                    <p>{preCountdownSeconds}</p>
                </div>
            {:else if isRecording}
                <div class="button" on:click={stopRecording}>
                    <img alt="stop button" src={'vectors/stop.svg'} />
                </div>

                <div class="spinner-container">
                    <Spinner />
                    <p>00:{secondsLeft}</p>
                </div>
            {:else if audioURL}
                <div class="audio-container">
                    <!-- svelte-ignore a11y-media-has-caption -->
                    <audio controls>
                        <source
                            src={audioURL}
                            type="audio/ogg"
                            kind="captions" />
                        Your browser does not support the audio element.
                    </audio>

                    <Button text="Delete" secondary onClick={deleteAudio} />
                </div>
            {:else}
                <div class="button" on:click={startRecording}>
                    <img alt="record button" src={'vectors/record.svg'} />
                </div>

                <p>
                    You haven't recorded your pitch yet. You have 1 minute to
                    talk about yourself and / or idea. (You don't need an idea)
                    Keep it concise, short and sweet 🍬
                </p>
            {/if}
        </div>
    </div>

    <Button
        primary
        text="Save"
        position="right"
        buttonType="submit"
        disabled={(!user.bio || !user.ideal ? true : false) || isSubmitting} />
</form>
